import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Calendar, Users, DollarSign, ChevronRight, Filter } from "react-feather";

import { listBatchesApi } from "../../../../Api/payrollApi";
import { checkStatusCodeSuccess, rendorBackButton } from "../../../../Constant/common";
import { PAYROLL_STRINGS, PAYROLL_STATUS } from "../../../../Constant/HR/payrollConstants";
import PayrollStatusBadge from "../../../../BaseComponents/PayrollStatusBadge";
import BaseButton from "../../../../BaseComponents/BaseButton";
import BaseSelect from "../../../../BaseComponents/BaseSelect";
import "./PayrollHistory.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatINR = (val) => {
  if (val == null || isNaN(val)) return "₹0";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

const getCurrentFY = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-based
  return month >= 4 ? year : year - 1;
};

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------

const SummaryCard = ({ icon: Icon, iconColor, iconBg, label, value, sub }) => (
  <div className="summary-card glass rounded-2xl animate-slide-up">
    <div aria-hidden="true" className="summary-card-icon rounded" style={{ background: iconBg }}>
      <Icon size={22} style={{ color: iconColor }} strokeWidth={2} />
    </div>
    <div className="summary-card-content">
      <p className="summary-card-label">{label}</p>
      <p className="summary-card-value">{value}</p>
      {sub && <p className="summary-card-sub">{sub}</p>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Status filter pill
// ---------------------------------------------------------------------------

const FilterPill = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`payroll-filter-pill ${active ? "active" : ""}`}
  >
    {label}
  </button>
);

// ---------------------------------------------------------------------------
// Batch row card
// ---------------------------------------------------------------------------

const BatchRow = ({ batch, onViewDetails, index }) => {
  const totalNet = batch?.summary?.totalNet ?? batch?.totalNet ?? 0;
  const totalGross = batch?.summary?.totalGross ?? batch?.totalGross ?? 0;
  const totalDeductions = batch?.summary?.totalDeductions ?? batch?.totalDeductions ?? 0;
  const totalEmployees = batch?.summary?.totalEmployees ?? batch?.totalEmployees ?? 0;
  const monthLabel = batch?.payrollMonth || `${batch?.month ?? ""}/${batch?.year ?? ""}`;

  return (
    <div
      className="batch-row glass rounded-2xl animate-slide-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Month */}
      <div className="batch-row-month">
        <p className="batch-row-month-title">{monthLabel}</p>
      </div>

      {/* Status */}
      <div className="flex-shrink-0">
        <PayrollStatusBadge status={batch?.status} />
      </div>

      {/* Employees */}
      <div className="batch-row-stat batch-row-stat-employees">
        <p className="batch-row-stat-label">Employees</p>
        <p className="batch-row-stat-value">{totalEmployees}</p>
      </div>

      {/* Gross */}
      <div className="batch-row-stat">
        <p className="batch-row-stat-label">Gross</p>
        <p className="batch-row-stat-value">{formatINR(totalGross)}</p>
      </div>

      {/* Deductions */}
      <div className="batch-row-stat">
        <p className="batch-row-stat-label">Deductions</p>
        <p className="batch-row-stat-value deduction">{formatINR(totalDeductions)}</p>
      </div>

      {/* Net */}
      <div className="batch-row-stat">
        <p className="batch-row-stat-label">Net</p>
        <p className="batch-row-stat-value net">{formatINR(totalNet)}</p>
      </div>

      {/* View Details */}
      <div className="batch-row-action">
        <BaseButton
          color="primary"
          onClick={() => onViewDetails(batch?.id)}
          aria-label={`View details for ${monthLabel}`}
        >
          View Details
          <ChevronRight size={14} aria-hidden="true" />
        </BaseButton>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const STATUS_FILTERS = ["All", PAYROLL_STATUS.DRAFT, PAYROLL_STATUS.APPROVED, PAYROLL_STATUS.PAID];

const PayrollHistory = () => {
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [yearFilter, setYearFilter] = useState("");

  // -------------------------------------------------------------------------
  // Fetch batches
  // -------------------------------------------------------------------------

  const fetchBatches = useCallback(() => {
    setIsLoading(true);
    listBatchesApi({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          const data = Array.isArray(resp?.data)
            ? resp.data
            : Array.isArray(resp?.data?.data)
              ? resp.data.data
              : [];
          // Sort reverse chronological
          const sorted = [...data].sort((a, b) => {
            const aDate = new Date(a.year ?? 0, (a.month ?? 1) - 1);
            const bDate = new Date(b.year ?? 0, (b.month ?? 1) - 1);
            return bDate - aDate;
          });
          setBatches(sorted);
        } else {
          toast.error(resp?.message || PAYROLL_STRINGS.ERROR_GENERIC);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || PAYROLL_STRINGS.ERROR_GENERIC);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchBatches(); // eslint-disable-line react-hooks/set-state-in-effect
  }, [fetchBatches]);

  // -------------------------------------------------------------------------
  // Derived summary values
  // -------------------------------------------------------------------------

  const currentFY = getCurrentFY();

  const fyBatches = batches.filter((b) => {
    const yr = Number(b.year ?? 0);
    const mo = Number(b.month ?? 0);
    // FY runs April (month 4) of currentFY to March (month 3) of currentFY+1
    return (yr === currentFY && mo >= 4) || (yr === currentFY + 1 && mo <= 3);
  });

  const totalNetFY = fyBatches.reduce((sum, b) => {
    return sum + (b?.summary?.totalNet ?? b?.totalNet ?? 0);
  }, 0);

  const mostRecentBatch = batches[0] ?? null;
  const uniqueEmployeeIds = new Set(
    batches.flatMap((b) =>
      Array.isArray(b?.employees) ? b.employees.map((e) => e?.staffId ?? e?.id) : [],
    ),
  );
  const employeeCount =
    uniqueEmployeeIds.size ||
    (mostRecentBatch?.summary?.totalEmployees ?? mostRecentBatch?.totalEmployees ?? 0);

  // -------------------------------------------------------------------------
  // Filtered batches
  // -------------------------------------------------------------------------

  const filteredBatches = batches.filter((b) => {
    const matchStatus = statusFilter === "All" || b.status === statusFilter;
    const matchYear = !yearFilter || String(b.year) === String(yearFilter);
    return matchStatus && matchYear;
  });

  // Year options from available batches
  const availableYears = [...new Set(batches.map((b) => b.year).filter(Boolean))].sort(
    (a, b) => b - a,
  );

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleViewDetails = (id) => {
    navigate(`/payrollBatch/${id}`);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="animate-fade-in d-flex flex-column gap-3">
      {/* Page header */}
      <div className="animate-slide-down d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="page-header-title">{PAYROLL_STRINGS.HISTORY_TITLE}</h1>
          <p className="page-header-subtitle">{PAYROLL_STRINGS.HISTORY_SUBTITLE}</p>
        </div>
        {rendorBackButton(() => navigate("/payrollTemplates"))}
      </div>

      {/* Summary cards */}
      <div className="stagger d-flex gap-3 flex-wrap">
        <SummaryCard
          icon={Calendar}
          iconColor="#6366f1"
          iconBg="rgba(99,102,241,0.12)"
          label="Latest Batch"
          value={
            mostRecentBatch
              ? mostRecentBatch.payrollMonth || `${mostRecentBatch.month}/${mostRecentBatch.year}`
              : "—"
          }
          sub={mostRecentBatch ? mostRecentBatch.status : undefined}
        />
        <SummaryCard
          icon={DollarSign}
          iconColor="#10b981"
          iconBg="rgba(16,185,129,0.12)"
          label={`Total Net (FY ${currentFY}-${String(currentFY + 1).slice(-2)})`}
          value={formatINR(totalNetFY)}
          sub={`${fyBatches.length} batch${fyBatches.length !== 1 ? "es" : ""}`}
        />
        <SummaryCard
          icon={Users}
          iconColor="#8b5cf6"
          iconBg="rgba(139,92,246,0.12)"
          label="Employee Count"
          value={employeeCount || "—"}
          sub="Most recent batch"
        />
      </div>

      {/* Filter bar */}
      <div className="payroll-filter-bar glass rounded-2xl animate-slide-up">
        <Filter size={15} aria-hidden="true" className="payroll-filter-icon" />
        <span className="payroll-filter-label">Status:</span>
        <div className="payroll-filter-pills">
          {STATUS_FILTERS.map((s) => (
            <FilterPill
              key={s}
              label={s}
              active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
            />
          ))}
        </div>

        {availableYears.length > 0 && (
          <>
            <span className="payroll-filter-label" style={{ marginLeft: "auto" }}>
              Year:
            </span>
            <div style={{ width: 120 }}>
              <BaseSelect
                name="yearFilter"
                value={yearFilter}
                handleChange={(name, val) => setYearFilter(val)}
                options={[
                  { value: "", label: "All Years" },
                  ...availableYears.map((y) => ({ value: String(y), label: String(y) })),
                ]}
                placeholder="All Years"
                mb="mb-0"
              />
            </div>
          </>
        )}
      </div>

      {/* Batch list */}
      {isLoading ? (
        <div className="payroll-loading-state glass rounded-2xl animate-fade-in">
          <span className="ds-spinner" role="status" aria-label="Loading batches" />
          <p className="payroll-loading-text">Loading payroll history…</p>
        </div>
      ) : filteredBatches.length === 0 ? (
        <div className="payroll-empty-state glass rounded-2xl animate-fade-in">
          {PAYROLL_STRINGS.EMPTY_BATCHES}
        </div>
      ) : (
        <div className="batch-list stagger">
          {filteredBatches.map((batch, index) => (
            <BatchRow
              key={batch?.id ?? index}
              batch={batch}
              index={index}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PayrollHistory;
